import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { fetchUser } = useAuth();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState("Processing...");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Auth processing started

        const hash = window.location.hash;

        // Direct Google OAuth flow: token comes from backend redirect
        const tokenMatch = hash.match(/token=([^&]+)/);
        if (tokenMatch) {
          const token = tokenMatch[1];
          const isExpert = hash.includes("is_expert=true");
          // Token received

          localStorage.setItem("xpert_token", token);
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

          setStatus("Finalizing login...");
          await fetchUser();

          // Honour an in-app "next" hint if the originating page set one
          // (see StepAccount.handleGoogle in GetVerifiedPage.jsx). Without
          // this, OAuth from mid-flow surfaces (the Get Verified wizard,
          // future booking-checkout flow, etc.) drops users on the
          // landing page instead of resuming where they were.
          //
          // Validation is paranoid because anything we navigate() to is
          // an open-redirect surface. Same checks as LoginPage.safeReturnTo:
          //   • must be a non-empty string
          //   • must start with "/" (relative path)
          //   • must NOT start with "//" (protocol-relative URL → unsafe)
          //
          // Always remove the key whether honoured or not — a stale value
          // would bounce the user back to /get-verified the next time they
          // signed in for any unrelated reason.
          let nextPath = null;
          try {
            const raw = localStorage.getItem("xpert_oauth_next");
            localStorage.removeItem("xpert_oauth_next");
            if (
              typeof raw === "string" &&
              raw.startsWith("/") &&
              !raw.startsWith("//")
            ) {
              nextPath = raw;
            }
          } catch { /* localStorage unavailable */ }

          const redirectPath =
            nextPath || (isExpert ? "/expert-dashboard" : "/");
          // Redirect after login
          window.history.replaceState(null, "", redirectPath);
          navigate(redirectPath, { replace: true });
          return;
        }

        // No token found
        setError("No authentication data found. Please try again.");
        setTimeout(() => navigate("/login", { replace: true }), 2000);
      } catch (error) {
        // Auth error occurred
        let errorMessage = "Authentication failed. Please try again.";
        if (error.message === "Network Error") {
          errorMessage = "Network error - please check your connection.";
        } else if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        }
        setError(errorMessage);
        setTimeout(() => navigate("/login", { replace: true }), 3000);
      }
    };

    processAuth();
  }, [navigate, fetchUser]);

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center">
      <div className="text-center">
        {error ? (
          <>
            <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-red-500 text-2xl">!</span>
            </div>
            <p className="text-red-400 mb-2">{error}</p>
            <p className="text-slateline-400 text-sm">Redirecting to login...</p>
          </>
        ) : (
          <>
            <div className="w-12 h-12 border-4 border-brand-green border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slateline-500">{status}</p>
          </>
        )}
      </div>
    </div>
  );
}
